import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Spinner } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartSchemaResponse } from '@/entities/dashboards';

type Props = {
  schema?: ChartSchemaResponse;
  isLoading: boolean;
};

/**
 * Справочник витрины данных: по нему пользователь пишет запрос диаграммы вручную.
 *
 * Свёрнут по умолчанию: запрос чаще правят точечно, уже зная состав данных, а развёрнутый
 * перечень представлений занял бы всю форму диаграммы.
 */
export const ChartSchemaReference: FC<Props> = ({ schema, isLoading }) => {
  const { t } = useTranslation('qualityDashboard');

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        {t('presets.editor.schemaLoading')}
      </div>
    );
  }

  if (!schema) return null;

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="schema">
        <AccordionTrigger className="text-sm">{t('presets.editor.schemaTitle')}</AccordionTrigger>
        <AccordionContent>
          <div className="flex max-h-72 flex-col gap-4 overflow-auto pr-2">
            {schema.views.map((view) => (
              <div key={view.name} className="flex flex-col gap-1">
                <p className="font-mono text-sm font-medium">{view.name}</p>
                <p className="text-xs text-muted-foreground">{view.description}</p>
                <ul className="flex flex-col gap-0.5">
                  {view.columns.map((column) => (
                    <li key={column.name} className="text-xs">
                      <span className="font-mono">{column.name}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        · {column.type} · {column.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {schema.attributes.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">{t('presets.editor.schemaAttributes')}</p>
                <ul className="flex flex-col gap-0.5">
                  {schema.attributes.map((attribute) => (
                    <li key={attribute.path} className="text-xs">
                      <span className="font-mono">{attribute.path}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        · {attribute.name} · {attribute.dataType}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {schema.expertReports.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">{t('presets.editor.schemaExpertReports')}</p>
                <ul className="flex flex-col gap-0.5">
                  {schema.expertReports.map((report) => (
                    <li key={report.id} className="text-xs">
                      {report.name}
                      <span className="text-muted-foreground">
                        {' '}
                        · {report.blocks.map((block) => block.name).join(', ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
